import { useState, type ReactNode } from 'react';
import type { SettingsPayload } from '../../shared/api.ts';
import { api, errorMessage } from '../lib/api.ts';
import { OptionRow, OptionRows, Segmented, Toggle } from '../components/Controls.tsx';

/**
 * A provider API key, set from here rather than from the server's `.env`.
 *
 * THREE RULES, and they are the reason this is a component rather than a text
 * input in a row:
 *
 * 1. **It saves itself.** Not part of the drawer's Save. A key is not a
 *    preference you revise alongside four others — it is a credential you paste
 *    once and expect to take effect, and half-saving one with a batch of
 *    unrelated settings is how you end up unsure whether it landed. It is also
 *    what makes the mode row above it work: the credential write returns fresh
 *    status blocks, so "API key" enables in the same drawer session with no Save
 *    in between and no loss of the drawer's other unsaved edits.
 * 2. **The value comes back masked, never whole.** The server has no route that
 *    returns a key; all this ever holds is `sk-…2345`. So the input starts
 *    EMPTY on Replace rather than pre-filled — there is nothing to pre-fill it
 *    with, and an input that looked pre-filled would imply otherwise.
 * 3. **Remove says what it falls back to.** Clearing the stored key reveals the
 *    server's env value again if there is one. That is a different outcome from
 *    "the studio now has no key", and the row has to distinguish them or Remove
 *    reads as destructive when it isn't.
 */
/** One credential as `GET /api/settings` reports it. The VALUE never crosses
 *  the wire — `hint` is a masked tail, and `source` says whether it was set in
 *  this drawer or comes from the server environment. */
export type Credential = {
  present?: boolean;
  source?: 'stored' | 'env';
  hint?: string;
  /** True when removing a STORED key would fall back to one in the server
   *  environment — said only when true, because Remove is then not destructive. */
  envFallback?: boolean;
};

/** One engine's availability block from the same payload. */
export type EngineStatus = { available?: boolean; reason?: string; issue?: string; model?: string | null };

export function CredentialRow({
  id,
  label,
  hint,
  cred,
  sub,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  cred?: Credential;
  /** OptionRow's indent flag — steps this row in under the one above. */
  sub?: boolean;
  /** The PUT answers with the refreshed status blocks — saving a key can make
   *  a whole engine available, so the caller MERGES them over what it holds
   *  (`settings` is not in that response and unsaved edits must survive it). */
  onChange: (payload: Partial<SettingsPayload>) => void;
}) {
  const present = Boolean(cred?.present);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function write(next: string | null) {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.put<Partial<SettingsPayload>>('/api/settings/credentials', { id, value: next });
      // Saving a key can make a whole ENGINE available, so the response carries
      // the status blocks and the caller merges them — otherwise the row above
      // still says the engine cannot run.
      onChange(res);
      setEditing(false);
      setValue('');
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const state = !present
    ? 'Not set on this server.'
    : cred?.source === 'stored'
      ? `Set here: ${cred?.hint}. It is stored on this server and never shown again.`
      : `From the server environment: ${cred?.hint}.`;

  return (
    <OptionRow label={label} sub={sub} stack hint={err ? null : `${hint} ${state}`}>
      {editing || !present ? (
        <div className="cred">
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Paste the key"
            aria-label={label}
            spellCheck={false}
            autoComplete="off"
            disabled={busy}
          />
          <button className="btn btn--sm" disabled={busy || !value.trim()} onClick={() => write(value)}>
            {busy ? 'Saving…' : 'Save key'}
          </button>
          {present && (
            <button
              className="btn btn--sm btn--ghost"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setValue('');
                setErr(null);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      ) : (
        <div className="cred">
          <code className="cred__hint">{cred?.hint}</code>
          <button className="btn btn--sm" disabled={busy} onClick={() => setEditing(true)}>
            Replace
          </button>
          {/* Only offered for a key STORED here. An env key is the deploy's, and
              this drawer does not get to delete it. */}
          {cred?.source === 'stored' && (
            <button className="btn btn--sm btn--ghost" disabled={busy} onClick={() => write(null)}>
              Remove
            </button>
          )}
        </div>
      )}
      {err && <p className="small error">{err}</p>}
      {/* Said only when it is true and only when it matters: Remove is not
          destructive here, and someone hovering over it deserves to know. */}
      {!err && present && cred?.source === 'stored' && cred.envFallback && (
        <p className="small muted">Removing it falls back to the key in the server environment.</p>
      )}
    </OptionRow>
  );
}

/**
 * ONE ENGINE'S ACCOUNT: which credential it spends, and the keys that back it.
 *
 * Used three times rather than written three times, because the three engines
 * are answering one question and a reader comparing them should not have to
 * work out whether two differently-shaped blocks mean the same thing. It opens
 * each engine's card in the drawer.
 *
 * IT USED TO BE ITS OWN GROUP, and the reason it could not stay one is the
 * reason the drawer is now organised by ENGINE (2026-08-10). Every other
 * setting belongs to the JOB it steers — a build model and a chat model are two
 * settings even when they name the same model. Auth is the one fact that steers
 * BOTH, so a drawer split by job had nowhere honest to put it: it lived in an
 * "Engines and keys" group of its own, and the Chat group had to write "on the
 * metered key set in Deck builds", a cross-reference to a group the reader was
 * not in. Grouping by engine puts the fact that steers both next to both, and
 * the cross-reference disappears rather than being reworded.
 *
 * THE WORDING IS "Subscription" AND "API key", ON EVERY ENGINE. Not "SDK": that
 * names the transport, not the account, and for Codex it is simply false — the
 * same `@openai/codex-sdk` client is constructed either way and only gains an
 * `apiKey`. Two engines answering the same question must read identically, or
 * the reader has to work out that "ChatGPT plan" and "SDK" are the same KIND of
 * answer. The plan's actual name lives in the hint underneath, which is where
 * this drawer already puts meaning.
 *
 * `mode: null` means the engine has no choice to offer (Fast is always metered).
 * A dial with one answer is a dead control, so it renders as a statement — but
 * still a statement: pass `fixedCopy` so it says what it costs like the others.
 */
export function EngineAccount({
  name,
  detail,
  status,
  offered = true,
  onOffered,
  offerCopy,
  mode,
  onMode,
  modeCopy,
  fixedCopy,
  apiCred,
  creds = [],
  credStatuses = {},
  onCredChange,
  control,
  extraRows,
  children,
}: {
  /** The engine as the user knows it — 'Claude', 'OpenCode', 'Pi'. */
  name: string;
  /** Whether this server offers the engine at all. FIRST row of the card,
   *  above the account, because it outranks it: what an engine costs stops
   *  mattering the moment nobody can reach it. Off takes the engine off BOTH
   *  surfaces — no builds, and the chat pill does not list it. */
  offered?: boolean;
  onOffered?: (next: boolean) => void;
  /** What each answer MEANS, on this engine. Written per engine because the
   *  reason to switch one off differs: Claude to keep other people's turns off
   *  this server's subscription, the metered engines to stop them spending. */
  offerCopy?: { on: ReactNode; off: ReactNode };
  detail?: ReactNode;
  status?: EngineStatus | null;
  /** Whose credential this engine spends: 'subscription' or 'api'. Null when
   *  the engine has only ONE legal mode — the row then renders `fixedCopy` as a
   *  statement rather than a dial with one answer. */
  mode?: string | null;
  onMode?: ((next: string) => void) | null;
  /** What each mode MEANS, keyed by mode. Says the price rather than
   *  describing the control. */
  modeCopy?: Record<string, string> | null;
  /** Used instead of `modeCopy` when the engine has only one legal mode — a
   *  dial with one answer renders as a statement, not a dead control. */
  fixedCopy?: ReactNode;
  /** The key the 'api' mode spends; its absence is what disables that mode. */
  apiCred?: Credential;
  creds?: { id: string; label: string; hint?: string; sub?: boolean }[];
  credStatuses?: Record<string, Credential | undefined>;
  onCredChange: (payload: Partial<SettingsPayload>) => void;
  /** An engine-specific control (a model select) rendered beside the mode. */
  control?: ReactNode;
  extraRows?: ReactNode;
  children?: ReactNode;
}) {
  const hasKey = Boolean(apiCred?.present);
  const unavailable = status && status.available === false;

  // What the head row says, in priority order: a live problem first, then the
  // consequence of the mode that is selected.
  //
  // The one state worth spelling out is a mode whose key has just been removed.
  // It is rare — it takes choosing the mode and then clearing the key — but it
  // is the only state where the drawer is showing a setting that cannot work,
  // and the person who caused it is standing right here.
  //
  // A switched-off engine says THAT and nothing about billing: an account row
  // describing what the next build costs, under a switch saying there will not
  // be one, is the drawer contradicting itself.
  const hint = !offered
    ? `${name} is switched off on this server: no builds, and the chat pill does not offer it.`
    : mode === 'api' && !hasKey
      ? `There is no key on this server, so ${name} cannot build or answer a message. Add a key below, or switch back to the subscription.`
      : unavailable
        ? status.issue
        : // `fixedCopy` is for an engine with no choice to state — it still owes
          // the reader the same sentence the others give, or it is the one block
          // that says nothing about what it costs.
          (mode ? modeCopy?.[mode] : null) || fixedCopy || null;

  return (
    <OptionRows>
      {/* THE OFFER SWITCH COMES FIRST, and it is the only row that is never
          about money. Everything under it — the account, the keys, the address
          — describes an engine that has to be on offer before any of it
          applies, which is also why none of those rows is hidden when it is
          off: the way back on must not be behind the door it just locked. */}
      {onOffered && (
        <OptionRow label={`Offer ${name}`} stack hint={offered ? offerCopy?.on : offerCopy?.off}>
          <Toggle checked={offered} onChange={onOffered} label={`Offer ${name}`} />
        </OptionRow>
      )}

      <OptionRow
        label={
          detail ? (
            <>
              {name} <span className="muted">{detail}</span>
            </>
          ) : (
            name
          )
        }
        stack
        hint={hint}
      >
        {mode
          ? // Two rules inside this control, both deliberate. The API-key segment
            // is disabled until a key exists, because the server refuses to store
            // that mode without one and offering it would be offering a 400. But
            // the CURRENTLY SELECTED segment is never disabled: a disabled active
            // segment reads as a broken control, and you cannot tell from it
            // which mode the studio is actually in.
            (() => (
              <Segmented
                value={mode ?? null}
                onChange={(next) => onMode?.(next)}
                label={`${name} account`}
                options={[
                  { value: 'subscription', label: 'Subscription' },
                  {
                    value: 'api',
                    label: 'API key',
                    disabled: !hasKey && mode !== 'api',
                    title: hasKey || mode === 'api' ? undefined : 'Add an API key below first.',
                  },
                ]}
              />
            ))()
          : control || null}
      </OptionRow>

      {/* The key rows are NOT gated on the engine being usable. In api mode with
          no key the engine reports itself unusable, and the key that fixes it is
          set right here — hiding the row until the engine works would leave the
          only exit behind the door it locks. */}
      {creds.map((cr) => (
        <CredentialRow
          key={cr.id}
          id={cr.id}
          sub
          label={cr.label}
          hint={cr.hint}
          cred={credStatuses[cr.id]}
          onChange={onCredChange}
        />
      ))}

      {/* Rows that belong WITH the keys rather than after them — the gateway's
          address is useless without its key and vice versa. */}
      {extraRows}

      {children}
    </OptionRows>
  );
}
